import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { Tooltip } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimesCircle,
  faClipboard,
  faClipboardCheck,
  faBell,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { Button } from "../App.styles";
import { MDBTooltip } from "mdb-react-ui-kit";
import { useDecision } from '@optimizely/react-sdk'

const List = styled.ul`
  width: 100%;
  display: flex;
  flex-wrap: wrap;

  li {
    margin-right: 1rem;
    margin-bottom: 1rem;

    @media (max-width: ${({ theme }) => theme.maxMobileWidth}) {
      margin-right: 0;
    }
  }

  @media (max-width: ${({ theme }) => theme.maxMobileWidth}) {
    ${Button} {
      overflow-wrap: anywhere;
      text-align: left;
      white-space: normal;
      word-break: normal;
    }
  }
`;

const RemoveTeam = styled(Button)`
    padding: 0;
    border-radius: 100%;
    line-height: 1;
    font-size: 1rem;
    margin-left: 1rem;
`

const TooltipParagraph = styled.p`
  padding-top: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.15rem;
  text-align: center;

  svg {
    margin-left: 0.625rem;
  }
`;

const NotificationTooltip = styled.span.withConfig({
    shouldForwardProp: (props) => !['active'].includes(props),
})`
    && {
        color: ${({ theme, active }) => active 
            ? theme.primaryColor 
            : theme.disabledColor};
        margin-left: 8px;
    }
`

const PERSONAL_QR_BASE_URL = 'https://app.socialscavenger.com/v1/services/qr.png';

const PlayerTooltip = ({ password, code, game_id }) => {
  const { t } = useTranslation();
  const [passwordCopied, setPasswordCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [qsCopied, setQSCopied] = useState(false);
  const [decisionPreviewLink] = useDecision('preview-links');
  const [decisionPersonalQrCode] = useDecision('personal_qr_code');

  const personalQrImageUrl = useMemo(() => {
    if (!game_id || !code) return null;
    const params = new URLSearchParams({
      whitelabel: process.env.REACT_APP_WHITELABEL || '',
      game_id: String(game_id),
      style: 'join',
      code,
    });
    return `${PERSONAL_QR_BASE_URL}?${params.toString()}`;
  }, [game_id, code]);

  const targetUrl = useMemo(() => {
    if (decisionPreviewLink?.enabled) {
        return `${process.env.REACT_APP_PREVIEW_URL}/#/?code=${code}&preview=yes`
    }
    return `${process.env.REACT_APP_PREVIEW_URL}/#/signin?code=${code}&preview=yes`
  }, [code, decisionPreviewLink])

  const copyPassword = () => {
    navigator.clipboard.writeText(password);
    setCodeCopied(false);
    setPasswordCopied(true);
  };

  const copyCode = () => {
    navigator.clipboard.readText();
    navigator.clipboard.writeText(code);
    setCodeCopied(true);
    setPasswordCopied(false);
  };

  const copyQSUrl = () => {
    navigator.clipboard.readText();
    navigator.clipboard.writeText(
        targetUrl
    );
    setQSCopied(true);
  };

  useEffect(() => {
    const copy = async () => {
      try {
        const clipboard = await navigator.clipboard.readText();

        if (clipboard === password) {
          setPasswordCopied(true);
        }

        if (clipboard === code) {
          setCodeCopied(true);
        }
      } catch (error) {
        console.warn(t("Silly clipboard thing threw an exception"), error);
      }
    };

    copy();
  }, [code, password, t]);

  const passwordIcon = passwordCopied ? faClipboardCheck : faClipboard;
  const codeIcon = codeCopied ? faClipboardCheck : faClipboard;
  const passwordHint = passwordCopied
    ? t("Copied to clipboard")
    : t("Copy to clipboard");
  const codeHint = codeCopied ? t("Copied to clipboard") : t("Copy to clipboard");

  return (
    <TooltipParagraph>
      <p>
        {password ? (
          <>
            {t('Password is')} {password}.
            <FontAwesomeIcon
              icon={passwordIcon}
              onClick={copyPassword}
              title={passwordHint}
            />
            <br />
          </>
        ) : (
          <span className="d-block">
            {t('Password is set by user and encrypted.')}
          </span>
        )}
        {t('Player can access game with their unique unlock code')} {code}.
        <FontAwesomeIcon icon={codeIcon} onClick={copyCode} title={codeHint} />
      </p>
      <p>
        <span>{t('Personal Link')}:{" "}</span>
        <a
          href={targetUrl}
          target="_blank"
          rel="noreferrer"
          style={{ color: "white" }}
        >
          {targetUrl}
        </a>
        <FontAwesomeIcon
          icon={qsCopied ? faClipboardCheck : faClipboard}
          onClick={copyQSUrl}
          title={qsCopied ? t("Copied to clipboard") : t("Copy to clipboard")}
        />
      </p>
      {decisionPersonalQrCode?.enabled && personalQrImageUrl && (
        <p>
          <span>{t('Personal QR Code')}:{" "}</span>
          <a
            href={personalQrImageUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "white" }}
          >
            {t('Download')}
            <FontAwesomeIcon icon={faDownload} title={t('Download QR code image')} />
          </a>
        </p>
      )}
    </TooltipParagraph>
  );
};

const Player = ({ children, title }) => {
  const [open, setOpen] = useState(false);
  const [fixed, setFixed] = useState(false);

  return (
    <Tooltip
      arrow
      interactive
      onClick={() => setFixed(!fixed)}
      onClose={() => {
        if (!fixed) {
          setOpen(false);
        }
      }}
      onOpen={() => setOpen(true)}
      open={open}
      title={title}
      data-testid="player__notification"
    >
      {children}
    </Tooltip>
  );
};

const PlayerList = ({ onRemove, users, mode = "view", game_id }) => {
  const { t } = useTranslation();
  return (
    <List className="list-unstyled list-inline">
      {users.map(({ id, email, game, name, password_generated, push_enabled }, key) => (
        <Player
          key={key}
          title={
            <PlayerTooltip password={password_generated} code={game?.code} game_id={game_id} />
          }
        >
          <li key={id}>
            <Button tag="span" color="light" tabIndex={0} data-testid="player__notification__button">
              {name === "Anonymous" || name === ""
                ? email
                : `${email} (${name})` || email}
              {onRemove && (
                <RemoveTeam 
                    onClick={() => onRemove(id)}
                    color="link"
                >
                    <FontAwesomeIcon
                        icon={faTimesCircle}
                    />
                </RemoveTeam>
              )}
              {
                mode === 'view'
                    ? (
                        <NotificationTooltip 
                            active={push_enabled} 
                        >
                            <MDBTooltip
                                tag="small" 
                                title={t(`${push_enabled 
                                    ? 'Push notifications enabled or previously enabled.' 
                                    : 'Push notifications disabled.'}`)}
                            >
                                <FontAwesomeIcon 
                                    icon={faBell}
                                />
                            </MDBTooltip>
                        </NotificationTooltip>
                    )
                    : null
              }
            </Button>
          </li>
        </Player>
      ))}
    </List>
  );
};

export default PlayerList;
